import { cached } from "@/lib/ssrCache";
import { supabase } from "@/integrations/supabase/client";
import { categoryConfig } from "@/lib/categoryConfig";
import { categoryAliases, mainCategoryAliases } from "@/lib/categoryAliases";

export interface ListingLink {
  path: string;
  label: string;
}

/**
 * Server-rendered link lists for listing pages.
 * These run inside route loaders so crawlers without JavaScript still see
 * contextual internal links to individual articles.
 */
export async function fetchLatestArticleLinks(limit = 60): Promise<ListingLink[]> {
  return cached(`latest-links:${limit}`, 5 * 60 * 1000, async () => {
  try {
    const { data, error } = await supabase
      .from("articles")
      .select("slug, title")
      .eq("status", "published")
      .order("published_at", { ascending: false })
      .limit(limit);
    if (error || !data) return [];
    return data
      .filter((a) => a.slug && a.title)
      .map((a) => ({ path: `/articles/${a.slug}`, label: a.title as string }));
  } catch {
    return [];
  }
  });
}

/**
 * Server-rendered "more from this category" links for an article page.
 * Guarantees every published article receives incoming internal links from
 * its siblings, so no canonical URL is left orphaned.
 */
async function fetchArticlePool(
  category: string | null | undefined,
): Promise<{ slug: string; title: string }[]> {
  return cached(`siblings:${category ?? "all"}`, 5 * 60 * 1000, async () => {
    let query = supabase
      .from("articles")
      .select("slug, title")
      .eq("status", "published")
      .order("published_at", { ascending: false })
      .limit(1000);
    if (category) query = query.eq("category", category);
    const { data, error } = await query;
    if (error || !data) return [] as { slug: string; title: string }[];
    return data.filter((a) => a.slug && a.title) as {
      slug: string;
      title: string;
    }[];
  });
}

export async function fetchRelatedArticleLinks(
  category: string | null | undefined,
  excludeSlug: string,
  limit = 12,
): Promise<ListingLink[]> {
  try {
    const out: ListingLink[] = [];
    const seen = new Set<string>([excludeSlug]);

    // Take a wrap-around window of neighbours around the current article so
    // every article in the category (old ones included) is linked from its
    // siblings, not just the newest handful. Small categories then top up
    // from the site-wide pool so no article is left without inbound links.
    const appendPool = (pool: { slug: string; title: string }[]) => {
      if (pool.length === 0) return;
      const idx = pool.findIndex((a) => a.slug === excludeSlug);
      const start = idx >= 0 ? idx : 0;
      for (let i = 1; out.length < limit && i <= pool.length; i++) {
        const s = pool[(start + i) % pool.length];
        if (!s || seen.has(s.slug)) continue;
        seen.add(s.slug);
        out.push({ path: `/articles/${s.slug}`, label: s.title });
      }
    };
    if (category) appendPool(await fetchArticlePool(category));
    // Most categories already supply every requested link; only fetch the
    // site-wide fallback when a genuinely small category needs topping up.
    if (out.length < limit) appendPool(await fetchArticlePool(null));
    return out;
  } catch {
    return [];
  }
}




function escapePgOrValue(value: string) {
  const escaped = value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  return `"${escaped}"`;
}

function categoryNamesFor(categorySlug: string): string[] {
  const config = categoryConfig[categorySlug];
  if (!config) return [];
  const names: string[] = [config.title, ...(mainCategoryAliases[categorySlug] || [])];
  for (const section of config.sections) {
    for (const link of section.links) {
      names.push(link.name);
      names.push(...(categoryAliases[link.slug] || []));
    }
  }
  return [...new Set(names)];
}

export async function fetchCategoryArticleLinks(
  categorySlug: string,
  limit = 60,
): Promise<ListingLink[]> {
  return cached(`category-links:${categorySlug}:${limit}`, 5 * 60 * 1000, () =>
    loadCategoryArticleLinks(categorySlug, limit),
  );
}

async function loadCategoryArticleLinks(
  categorySlug: string,
  limit: number,
): Promise<ListingLink[]> {
  const names = categoryNamesFor(categorySlug);
  if (names.length === 0) return [];
  try {
    const list = names.map(escapePgOrValue).join(",");
    const { data, error } = await supabase
      .from("articles")
      .select("slug, title")
      .eq("status", "published")
      .or(`category.in.(${list}),categories.ov.{${list}}`)
      .order("published_at", { ascending: false })
      .limit(limit);
    if (error || !data) return [];
    return data
      .filter((a) => a.slug && a.title)
      .map((a) => ({ path: `/articles/${a.slug}`, label: a.title as string }));
  } catch {
    return [];
  }
}

/** Subcategory hub links for a category, rendered server-side. */
export function categorySubLinks(categorySlug: string): ListingLink[] {
  const config = categoryConfig[categorySlug];
  if (!config) return [];
  const out: ListingLink[] = [];
  for (const section of config.sections) {
    for (const link of section.links) {
      out.push({
        path: link.href ?? `/category/${categorySlug}?sub=${link.slug}`,
        label: link.name,
      });
    }
  }
  return out;
}
